import { showNotification } from '@mantine/notifications';
import { IconCheck, IconInfoCircle, IconAlertTriangle, IconX } from '@tabler/icons-react';

// Centralized toast helper with consistent defaults for title, icon, and duration
// Usage:
//  - toast.success('Saved')
//  - toast.info('Heads up', { title: 'Notice' })
//  - toast.show({ message: 'Custom', color: 'gray' }) // mapped to info defaults

const typeDefaults = {
  success: { title: 'Success', color: 'green', icon: <IconCheck size={16} /> , autoClose: 2500 },
  info: { title: 'Info', color: 'blue', icon: <IconInfoCircle size={16} /> , autoClose: 3000 },
  warn: { title: 'Warning', color: 'yellow', icon: <IconAlertTriangle size={16} /> , autoClose: 4000 },
  error: { title: 'Error', color: 'red', icon: <IconX size={16} /> , autoClose: 5000 },
};

function withDefaults(base = {}, fallbackType = 'info') {
  const d = typeDefaults[fallbackType] || typeDefaults.info;
  const autoClose = base.autoClose != null ? base.autoClose : d.autoClose;
  return {
    title: base.title || d.title,
    color: base.color || d.color,
    icon: base.icon || d.icon,
    withCloseButton: base.withCloseButton != null ? base.withCloseButton : true,
    radius: base.radius != null ? base.radius : 'md',
    autoClose,
    ...base,
  };
}

function inferTypeFromColor(color) {
  const c = String(color || '').toLowerCase();
  if (['green', 'teal'].includes(c)) return 'success';
  if (['yellow', 'orange'].includes(c)) return 'warn';
  if (['red'].includes(c)) return 'error';
  return 'info';
}

export const toast = {
  // Generic entry point that maps color to a default type if title/icon missing
  show: (opts = {}) => {
    const type = inferTypeFromColor(opts.color);
    return showNotification(withDefaults(opts, type));
  },
  success: (message, opts = {}) => showNotification(withDefaults({ message, ...opts, color: opts.color || 'green' }, 'success')),
  info: (message, opts = {}) => showNotification(withDefaults({ message, ...opts, color: opts.color || 'blue' }, 'info')),
  warn: (message, opts = {}) => showNotification(withDefaults({ message, ...opts, color: opts.color || 'yellow' }, 'warn')),
  error: (message, opts = {}) => showNotification(withDefaults({ message, ...opts, color: opts.color || 'red' }, 'error')),
};
